import { useRef, useEffect, useState } from "react"
import { XCircle, File } from "lucide-react"

import { directFileUpload, setupDropZone } from "~/common/directFileUpload"
import { cn } from "~/common/shadcn-utils"
import { Button } from "~/shadcn/ui/button"
import { Divider } from "~/ui/Divider"
import { useTestFlow } from "./TestFlowProvider"
import { ContinueOrSaveBar } from "./ContinueOrSaveBar"
import DOCFileIcon from "~/images/icons/doc_file.svg?react"
import DOCXFileIcon from "~/images/icons/docx_file.svg?react"
import PPTFileIcon from "~/images/icons/ppt_file.svg?react"
import PPTXFileIcon from "~/images/icons/pptx_file.svg?react"
import XLSFileIcon from "~/images/icons/xls_file.svg?react"
import XLSXFileIcon from "~/images/icons/xlsx_file.svg?react"
import PDFFileIcon from "~/images/icons/pdf_file.svg?react"

const getFileIcon = (fileName: string) => {
  const extension = fileName.split(".").pop()?.toLowerCase()

  switch (extension) {
    case "pdf":
      return <PDFFileIcon width={50} height={50} />
    case "doc":
      return <DOCFileIcon width={50} height={50} />
    case "docx":
      return <DOCXFileIcon width={50} height={50} />
    case "xls":
      return <XLSFileIcon width={50} height={50} />
    case "xlsx":
      return <XLSXFileIcon width={50} height={50} />
    case "ppt":
      return <PPTFileIcon width={50} height={50} />
    case "pptx":
      return <PPTXFileIcon width={50} height={50} />
    default:
      return <File width={50} height={50} />
  }
}

const getFileTypeName = (fileName: string) => {
  const extension = fileName.split(".").pop()?.toLowerCase()

  switch (extension) {
    case "pdf":
      return "PDF document"
    case "doc":
    case "docx":
      return "Word document"
    case "xls":
    case "xlsx":
      return "Excel spreadsheet"
    case "ppt":
    case "pptx":
      return "PowerPoint presentation"
    default:
      return "Document"
  }
}

const formatFileSize = (bytes: number) => {
  if (bytes < 1024 * 1024) {
    return `${Math.round(bytes / 1024)} KB`
  }
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

export const DocumentUploadResponseForm = ({
  timeExpired,
  onStopTimer,
}: {
  timeExpired: boolean
  onStopTimer: () => void
}) => {
  const { answerQuestion } = useTestFlow()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [navigatedAwayCount, setNavigatedAwayCount] = useState(0)

  const dropZoneRef = useRef<HTMLDivElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [currentUpload, setCurrentUpload] = useState<{
    id: string
    file: File
    progress: number
    signedId?: string
  } | null>(null)

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        setNavigatedAwayCount((prevCount) => prevCount + 1)
      }
    }

    document.addEventListener("visibilitychange", handleVisibilityChange)

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange)
    }
  }, [])

  const onClick = async () => {
    setLoading(true)
    if (!timeExpired && !currentUpload?.signedId) {
      setError("Please upload a file before continuing")
      setLoading(false)
      return
    }

    onStopTimer()
    setError(null)

    try {
      await answerQuestion({
        selectedChoiceIds: [],
        document: currentUpload?.signedId,
        navigatedAwayCount: navigatedAwayCount,
      })
    } catch (err) {
      setError("Failed to submit your answer. Please try again.")
      console.error(err)
    }

    setLoading(false)
  }

  const processUpload = async (file: File) => {
    // Clear any existing upload
    setCurrentUpload(null)
    setError(null)

    const id = Math.random().toString(36).substring(2, 15)

    try {
      // Start upload
      setCurrentUpload({
        id,
        file,
        progress: 0,
      })

      const result = await directFileUpload(file, {
        onProgress: ({ progress }) => {
          setCurrentUpload((prev) =>
            prev?.id === id ? { ...prev, progress } : prev
          )
        },
      })

      setCurrentUpload((prev) =>
        prev?.id === id ? { ...prev, signedId: result.signedId } : prev
      )
    } catch (error) {
      console.error("Upload failed:", error)
      setError(
        `Failed to upload: ${
          error instanceof Error ? error.message : "Unknown error"
        }`
      )
      setCurrentUpload(null)
    }
  }

  useEffect(() => {
    if (!dropZoneRef.current) return

    const cleanup = setupDropZone(dropZoneRef.current, {
      onStart: (_, file) => {
        // For drag and drop, only process the first file since we only allow 1 document per question
        processUpload(file)
      },
    })

    return cleanup
  }, [dropZoneRef])

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (!event.target.files?.length) return

    const file = event.target.files[0]
    await processUpload(file)

    if (fileInputRef.current) {
      fileInputRef.current.value = ""
    }
  }

  const handleRemoveFile = () => {
    setCurrentUpload(null)
    setError(null)
    if (fileInputRef.current) {
      fileInputRef.current.value = ""
    }
  }

  return (
    <div>
      <div className="mb-4">Answer</div>

      {!currentUpload ? (
        <div
          ref={dropZoneRef}
          className={cn(
            "drop-zone p-8 border border-dashed border-blue-400 bg-blue-100 rounded-lg text-center",
            { "opacity-50": timeExpired }
          )}
        >
          <div className="flex flex-col gap-4 w-full md:w-1/2 mx-auto">
            <p className="font-medium text-3xl">Drag & Drop Document Here</p>
            <div className="py-3 flex items-center text-xs text-black font-medium before:flex-1 before:border-t before:border-blue-300 before:me-4 after:flex-1 after:border-t after:border-blue-300 after:ms-4">
              or
            </div>
            <input
              ref={fileInputRef}
              type="file"
              className="hidden"
              onChange={handleFileChange}
              disabled={timeExpired}
              accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx"
            />
            <div className="flex items-center justify-center">
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="px-5 py-4 bg-blue-500 text-white font-medium rounded text-xl"
                disabled={timeExpired}
              >
                Browse Computer
              </button>
            </div>
            <div className="flex flex-col gap-0 mt-4">
              <p className="text-sm">
                Supported file types: .xlsx / .docx / .pptx / .pdf
              </p>
              <p className="text-sm">Max file size 25 MB</p>
            </div>
          </div>
        </div>
      ) : (
        <div className="bg-gray-F7F7F5 border border-gray-E6E6E3 mt-4 rounded p-4">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              {getFileIcon(currentUpload.file.name)}
            </div>
            <div className="flex flex-col w-full">
              <div className="flex justify-between items-center">
                <div className="flex flex-col gap-0.5">
                  <span className="font-medium">{currentUpload.file.name}</span>
                  <span className="text-xs text-gray-500">
                    {getFileTypeName(currentUpload.file.name)}
                    {" - "}
                    {formatFileSize(currentUpload.file.size)}
                  </span>
                </div>
                {!timeExpired && (
                  <button
                    onClick={handleRemoveFile}
                    className="text-gray-8E8E92"
                    aria-label="Remove file"
                  >
                    <XCircle size={16} />
                  </button>
                )}
              </div>

              <div className="mt-2 flex items-center gap-2">
                <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div
                    className={cn(
                      "h-full",
                      currentUpload.signedId ? "bg-blue-500" : "bg-blue-500"
                    )}
                    style={{ width: `${currentUpload.progress}%` }}
                  />
                </div>
                <span className="text-sm w-10 text-right">
                  {currentUpload.signedId
                    ? "100%"
                    : `${currentUpload.progress}%`}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {error && <div className="text-red-500 mt-2">{error}</div>}

      <Divider className="my-10" />

      <ContinueOrSaveBar>
        <Button
          isLoading={loading}
          disabled={loading || timeExpired || !currentUpload?.signedId}
          onClick={onClick}
        >
          Continue
        </Button>
      </ContinueOrSaveBar>
    </div>
  )
}
